import React, { useRef, useState } from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { FileText } from 'lucide-react';

interface FileDropZoneProps {
  onFileSelected: (file: File, base64: string) => void;
  accept?: string;
  maxSizeMB?: number;
  /** Icona custom (default: FileText) */
  icon?: React.ReactNode;
  /** Titolo principale (default: "Trascina file o clicca per selezionare") */
  title?: string;
  /** Subtitle con dettagli formato (default: per CSV) */
  subtitle?: string;
  /** File già selezionato — mostra preview invece di drop zone */
  selectedFile?: File | null;
}

/**
 * Drop-zone riusabile per upload file (CSV/GPS/altro).
 * Drag-drop + click + reader base64 + size validation.
 */
export const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFileSelected,
  accept = '.csv,.tsv,.txt',
  maxSizeMB = 10,
  icon,
  title = 'Trascina file o clicca per selezionare',
  subtitle = 'Formati: .csv .tsv .txt · Separatore: virgola, tab, punto-virgola',
  selectedFile,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File > ${maxSizeMB} MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const b64 = result.split(',')[1] ?? result;
      onFileSelected(file, b64);
    };
    reader.onerror = () => setError('Errore lettura file');
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <Box
        border="2px dashed"
        borderColor={isDragging ? 'green.500' : selectedFile ? 'green.400' : 'gray.300'}
        bg={isDragging ? 'green.50' : selectedFile ? 'green.50' : 'gray.50'}
        borderRadius="lg"
        p={selectedFile ? 6 : 10}
        textAlign="center"
        cursor="pointer"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
        transition="all 0.15s"
      >
        <VStack gap={2}>
          <Box color={selectedFile ? 'green.600' : 'gray.500'}>
            {icon ?? <FileText size={32} />}
          </Box>
          <Text fontSize="sm" fontWeight="600" color={selectedFile ? 'green.700' : 'gray.700'}>
            {selectedFile ? selectedFile.name : title}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {selectedFile
              ? `${(selectedFile.size / 1024).toFixed(1)} KB · clicca per cambiare`
              : subtitle}
          </Text>
          {!selectedFile && (
            <Text fontSize="xs" color="gray.400">
              Max {maxSizeMB} MB
            </Text>
          )}
        </VStack>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      {error && (
        <Text mt={2} color="red.500" fontSize="sm">
          {error}
        </Text>
      )}
    </Box>
  );
};
